/**
 * cryptoUtils.js — Utilitário de Criptografia Client-Side (Web Crypto API)
 *
 * Calcula o hash SHA-256 de um File ou Blob usando window.crypto.subtle.digest.
 * Retorna a string hexadecimal lowercase do hash completo (64 caracteres).
 *
 * Consumidores:
 *   - src/modules/files/FileHubArea.jsx (handlePublishPetition — Fase A/C)
 *   - Qualquer fluxo futuro que exija prova de integridade documental.
 */

/**
 * Calcula o SHA-256 de um File ou Blob.
 * @param {File|Blob} fileOrBlob - O arquivo ou blob a ser hasheado.
 * @returns {Promise<string>} Hash SHA-256 em hexadecimal lowercase (64 chars).
 */
export async function computeFileSHA256(fileOrBlob) {
  if (!fileOrBlob || typeof fileOrBlob.arrayBuffer !== 'function') {
    throw new Error('computeFileSHA256: argumento inválido — esperado File ou Blob.');
  }

  const buffer = await fileOrBlob.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Formata um hash SHA-256 para exibição truncada.
 * Ex: "a1b2c3d4e5f6g7h8...x9y0z1w2a3b4c5d6"
 * @param {string} hash - Hash completo (64 chars).
 * @param {number} [chars=8] - Caracteres a exibir no início e no fim.
 * @returns {string} Hash truncado para exibição.
 */
export function formatHashDisplay(hash, chars = 8) {
  const safe = String(hash || '').trim().toLowerCase();
  if (!safe) return '';
  if (safe.length <= chars * 2 + 3) return safe;
  return `${safe.slice(0, chars)}...${safe.slice(-chars)}`;
}
